import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  MarkEmailRead as MarkEmailReadIcon,
  Refresh as RefreshIcon,
  VerifiedUser as VerifiedUserIcon,
} from "@mui/icons-material";

import { sendVerificationCode, verifyEmailCode } from "_DatabaseFunctions/cloudCalls/auth";

function VerifyEmailCodePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const user = useSelector((state: any) => state.user.user);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error" | "info">("info");
  const [isLoading, setLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Focus on code input when component mounts
  useEffect(() => {
    if (codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, []);

  const sendCode = async () => {
    if (!user?.email || !user?.displayName) {
      setMsg("User information not available. Please sign in again.");
      setMsgType("error");
      return;
    }

    setIsResending(true);
    setMsg("");
    try {
      await sendVerificationCode({
        userId: user.uid,
        email: user.email,
      });
      setMsg(`Verification code sent to ${user.email}`);
      setMsgType("success");
    } catch (error: any) {
      setMsg(error.message || "Failed to send verification code");
      setMsgType("error");
    } finally {
      setIsResending(false);
    }
  };

  const verifyCode = async () => {
    if (!code.trim()) {
      setMsg("Please enter the verification code");
      setMsgType("error");
      return;
    }

    if (code.length !== 6) {
      setMsg("Verification code must be 6 digits");
      setMsgType("error");
      return;
    }

    setLoading(true);
    setMsg("");
    try {
      await verifyEmailCode({
        userId: user.uid,
        code: code.trim(),
      });
      setMsg("Email verified successfully! Redirecting...");
      setMsgType("success");
      setIsVerified(true);
      
      // Refresh the page after a delay to trigger auth state update
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      setMsg(error.message || "Invalid verification code");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !isLoading && code.length === 6) {
      verifyCode();
    }
  };

  if (isVerified) {
    return (
      <Box sx={{ py: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 600, mx: "auto", textAlign: "center" }}>
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <VerifiedUserIcon 
                color="success" 
                sx={{ fontSize: 64, mb: 2 }} 
              />
              <Typography variant="h5" color="success.main" gutterBottom>
                Email Verified!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your email has been successfully verified. You will be redirected shortly.
              </Typography>
              <CircularProgress sx={{ mt: 3 }} color="success" />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "primary.main",
            fontWeight: 600,
          }}
        >
          <MarkEmailReadIcon
            color="primary"
            fontSize={isMobile ? "large" : "inherit"}
          />
          Verify Your Email
        </Typography>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          color="text.primary"
          sx={{
            fontWeight: 400,
            mb: 1,
          }}
        >
          Hello {user?.displayName || "User"}!
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ maxWidth: 600, mx: "auto" }}
        >
          Enter the 6-digit verification code sent to your email
        </Typography>
      </Box>

      {/* Verification Card */}
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              We've sent a 6-digit verification code to:
            </Typography>

            <Typography
              variant="h6"
              color="primary.main"
              sx={{ mb: 3, fontWeight: 500 }}
            >
              {user?.email}
            </Typography>

            {/* Code Input */}
            <Box sx={{ mb: 3 }}>
              <TextField
                ref={codeInputRef}
                value={code}
                onChange={handleCodeChange}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: "center",
                    fontSize: "2rem",
                    fontWeight: 600,
                    letterSpacing: "0.5rem",
                    fontFamily: "monospace",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "2rem",
                  },
                  "& .MuiInputBase-input": {
                    padding: "16px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VerifiedUserIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                disabled={isLoading}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {code.length === 0
                ? "Enter the 6-digit code from your email"
                : `${code.length}/6 digits entered`}
            </Typography>

            {/* Verify Button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={verifyCode}
              disabled={isLoading || code.length !== 6}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                borderRadius: 2,
                mb: 2,
                minWidth: 200,
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Verify Email"
              )}
            </Button>

            {/* Resend Section */}
            <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Didn't receive the code? Check your spam folder or:
              </Typography>

              <Button
                variant="outlined"
                color="primary"
                onClick={sendCode}
                disabled={isResending}
                startIcon={isResending ? <CircularProgress size={16} /> : <RefreshIcon />}
                sx={{
                  borderRadius: 2,
                }}
              >
                {isResending ? "Sending..." : "Resend Code"}
              </Button>
            </Box>

            {/* Message Display */}
            {msg && (
              <Alert
                severity={msgType}
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  textAlign: "left",
                  "& .MuiAlert-message": {
                    fontSize: "1rem",
                  },
                }}
              >
                {msg}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default VerifyEmailCodePage;